<template>
  <header class="header">
    <div class="header-container">
      <!-- Logo区域 -->
      <div class="logo">
        <NuxtLink :to="localePath('/')" class="logo-link">
          <img src="/images/common/logo.png" alt="logo" class="logo-icon" width="32" height="32" />
          <span class="logo-text">{{ $t('nav.brand') }}</span>
        </NuxtLink>
      </div>

      <!-- 导航菜单区域 -->
      <nav class="nav">
        <div class="nav-item dropdown">
          <button class="nav-link dropdown-toggle" aria-haspopup="true" aria-expanded="false">
            <Icon name="material-symbols:work" size="16" />
            {{ $t('nav.office') }}
            <Icon name="material-symbols:expand-more" class="dropdown-icon" />
          </button>
          <div class="dropdown-menu">
            <NuxtLink :to="localePath('/translate-office')" class="dropdown-item">{{ $t('nav.dropdown.word') }}</NuxtLink>
            <NuxtLink :to="localePath('/translate-office')" class="dropdown-item">{{ $t('nav.dropdown.ppt') }}</NuxtLink>
            <NuxtLink :to="localePath('/translate-office')" class="dropdown-item">{{ $t('nav.dropdown.excel') }}</NuxtLink>
          </div>
        </div>

        <NuxtLink :to="localePath('/translate-pdf')" class="nav-link">
          <Icon name="material-symbols:picture-as-pdf" size="16" />
          {{ $t('nav.pdf') }}
        </NuxtLink>

        <div class="nav-item dropdown">
          <button class="nav-link dropdown-toggle" aria-haspopup="true" aria-expanded="false">
            <Icon name="material-symbols:menu-book" size="16" />
            {{ $t('nav.ebook') }}
            <Icon name="material-symbols:expand-more" class="dropdown-icon" />
          </button>
          <div class="dropdown-menu">
            <NuxtLink :to="localePath('/translate-ebook')" class="dropdown-item">{{ $t('nav.dropdown.epub') }}</NuxtLink>
            <NuxtLink :to="localePath('/translate-ebook')" class="dropdown-item">{{ $t('nav.dropdown.mobi') }}</NuxtLink>
            <NuxtLink :to="localePath('/translate-ebook')" class="dropdown-item">{{ $t('nav.dropdown.azw3') }}</NuxtLink>
          </div>
        </div>

        <div class="nav-item dropdown">
          <button class="nav-link dropdown-toggle" aria-haspopup="true" aria-expanded="false">
            <Icon name="material-symbols:subtitles" size="16" />
            {{ $t('nav.subtitle') }}
            <Icon name="material-symbols:expand-more" class="dropdown-icon" />
          </button>
          <div class="dropdown-menu">
            <NuxtLink :to="localePath('/translate-subtitle')" class="dropdown-item">{{ $t('nav.dropdown.srt') }}</NuxtLink>
            <NuxtLink :to="localePath('/translate-subtitle')" class="dropdown-item">{{ $t('nav.dropdown.vtt') }}</NuxtLink>
            <NuxtLink :to="localePath('/translate-subtitle')" class="dropdown-item">{{ $t('nav.dropdown.ass') }}</NuxtLink>
          </div>
        </div>
        <NuxtLink :to="localePath('/blog')" class="nav-link">
          <Icon name="material-symbols:article-outline" size="16" />
          {{ $t('nav.blog') }}
        </NuxtLink>
        <NuxtLink :to="localePath('/pay')" class="nav-link">
          <Icon name="material-symbols:workspace-premium" size="16" />
          {{ $t('nav.pricing') }}
        </NuxtLink>
      </nav>

      <!-- 用户状态区域 -->
      <div class="user-actions">
        <ClientOnly>
          <template #fallback>
            <div class="user-actions-skeleton">
              <div class="skeleton-pill" />
            </div>
          </template>

          <template v-if="!userStore.isLoggedIn">
            <button class="btn btn-primary" @click="openAuth('login')">
              <Icon name="material-symbols:login" size="16" />
              {{ $t('auth.login') }}
            </button>
          </template>
          <template v-else>
            <!-- 剩余页数徽章（登录后显示） -->
            <NuxtLink v-if="isMembershipReady" :to="localePath('/my-page/membership')" class="quota-pill" :title="$t('header.viewQuota')">
              <Icon name="material-symbols:description-outline" size="16" />
              {{ $t('header.quota', { n: myPageStore.membershipInfo.remainingPages }) }}
            </NuxtLink>
            <UserDropdown
              :user="{ id: userStore.info?.id || userStore.loginData?.uid || '', name: userStore.userName, avatarUrl: userStore.userAvatar }"
              :open="userStore.dropdownOpen"
              :is-vip="isVip"
              @toggle="userStore.setDropdownOpen(!userStore.dropdownOpen)"
              @select="handleMenuSelect"
            />
          </template>
        </ClientOnly>
      </div>
      <!-- 语言切换器：独立占位，贴最右 -->
      <LanguageSwitcher />
    </div>
  </header>

  <!-- 认证弹窗 -->
  <AuthModal
    v-if="userStore.authModalVisible"
    :visible="userStore.authModalVisible"
    @close="closeAuth"
  />
</template>

<script setup lang="ts">
import { onMounted, watch, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '~/stores/user'
import { useMyPageStore } from '~/stores/myPage'
import UserDropdown from '~/components/shared/UserDropdown.vue'
import AuthModal from '~/components/shared/AuthModal.vue'

// @nuxt/icon 模块已自动提供 <Icon> 组件，无需手动导入

const userStore = useUserStore()
const myPageStore = useMyPageStore()
const router = useRouter()
const localePath = useLocalePath()

onMounted(() => {
  // 恢复登录态
  userStore.initFromStorage()
  // 登录态恢复后拉取一次配额
  if (userStore.isLoggedIn && !userStore.userLoaded && !userStore.userLoading) {
    userStore.fetchUserInfoFromUserMix()
  }
})

watch(
  () => userStore.isLoggedIn,
  (v) => {
    if (v && !userStore.userLoaded && !userStore.userLoading) {
      userStore.fetchUserInfoFromUserMix()
    }
  }
)

const isMembershipReady = computed(() => myPageStore.membershipLoaded && !myPageStore.membershipLoading)
const isVip = computed(() => myPageStore.membershipInfo.level === 'vip')

const openAuth = (mode: 'login' | 'register') => {
  // 简化处理：同一个弹窗，初始模式由内部切换（默认登录）
  userStore.setAuthModalVisible(true)
}

const closeAuth = () => {
  userStore.setAuthModalVisible(false)
}

const handleMenuSelect = async (key: 'my-page' | 'pay' | 'history' | 'logout') => {
  switch (key) {
    case 'my-page':
      router.push(localePath('/my-page'))
      break
    case 'pay':
      router.push(localePath('/pay'))
      break
    case 'history':
      router.push(localePath('/my-page/records'))
      break
    case 'logout':
      await userStore.logout()
      router.push(localePath('/'))
      break
  }
  userStore.setDropdownOpen(false)
}
</script>

<style scoped lang="scss">
.header {
  position: sticky;
  top: 0;
  z-index: 100;
  background-color: $white;
  border-bottom: 1px solid $gray-200;
  box-shadow: $shadow-sm;
}

.header-container {
  width: 100%;
  margin: 0;
  padding: 0 10px; // 左右各10px内边距
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 60px;
  gap: $spacing-md;
}

// Logo区域样式
.logo {
  .logo-link {
    display: flex;
    align-items: center;
    text-decoration: none;
    color: $gray-900;
  }

  .logo-icon {
    margin-right: $spacing-sm;
    width: 32px;
    height: 32px;
  }

  .logo-text {
    font-size: 1.25rem;
    font-weight: 600;
    color: $primary-color;
  }
}

// 导航区域样式
.nav {
  display: flex;
  align-items: center;
  gap: 0; // 去掉导航项间距

  .nav-item {
    position: relative;
  }

  .nav-link {
    padding: $spacing-sm $spacing-md;
    text-decoration: none;
    color: $gray-700;
    font-weight: 500;
    font-size: 14px;
    border-radius: $border-radius-sm;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    gap: $spacing-xs;

    .iconify {
      color: $primary-color;
    }

    &:hover {
      background-color: $gray-100;
      color: $primary-color;

      .iconify {
        color: $accent-color;
      }
    }

    &.router-link-active {
      color: $primary-color;
      background-color: rgba($primary-color, 0.1);

      .iconify {
        color: $accent-color;
      }
    }
  }

  // 下拉菜单样式
  .dropdown {
    .dropdown-toggle {
      background: none;
      border: none;
      cursor: pointer;
      padding: $spacing-sm $spacing-md;
      color: $gray-700;
      font-weight: 500;
      font-size: 14px;
      border-radius: $border-radius-sm;
      transition: all 0.2s ease;
      display: flex;
      align-items: center;
      gap: $spacing-xs;

      .iconify {
        color: $primary-color;
      }

      &:hover {
        background-color: $gray-100;
        color: $primary-color;

        .iconify {
          color: $accent-color;
        }
      }
    }

    .dropdown-icon {
      font-size: 16px;
      transition: transform 0.2s ease;
      color: $accent-color;
    }

    .dropdown-menu {
      position: absolute;
      top: 100%;
      left: 0;
      background-color: $white;
      border: 1px solid $gray-200;
      border-radius: $border-radius-md;
      box-shadow: $shadow-lg;
      min-width: 160px;
      padding: $spacing-sm 0;
      opacity: 0;
      visibility: hidden;
      transform: translateY(-8px);
      transition: all 0.2s ease;
      z-index: 1000;
    }

    &:hover .dropdown-menu {
      opacity: 1;
      visibility: visible;
      transform: translateY(0);
    }

    &:hover .dropdown-icon {
      transform: rotate(180deg);
    }

    .dropdown-item {
      display: block;
      padding: $spacing-sm $spacing-md;
      color: $gray-700;
      text-decoration: none;
      transition: all 0.2s ease;

      &:hover {
        background-color: $gray-100;
        color: $primary-color;
      }
    }
  }
}

// 用户操作区域样式
.user-actions {
  display: flex;
  align-items: center;
  gap: $spacing-sm;

  // SSR 占位，避免闪烁/抖动
  .user-actions-skeleton {
    height: 36px; // 接近按钮高度
    display: flex;
    align-items: center;
    .skeleton-pill {
      width: 96px;
      height: 32px;
      border-radius: 999px;
      background: linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 37%, #f3f4f6 63%);
      background-size: 400% 100%;
      animation: skeleton-loading 1.2s ease-in-out infinite;
    }
  }

  .btn {
    display: flex;
    align-items: center;
    gap: $spacing-xs;
    padding: $spacing-sm $spacing-md;
    border-radius: $border-radius-sm;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    text-decoration: none;
    border: 1px solid transparent;

    &.btn-outline {
      background-color: transparent;
      border-color: $gray-300;
      color: $gray-700;

      .iconify {
        color: $primary-color;
      }

      &:hover {
        border-color: $primary-color;
        color: $primary-color;

        .iconify {
          color: $accent-color;
        }
      }
    }

    &.btn-primary {
      background-color: $primary-color;
      color: $white;
      border-color: $primary-color;

      .iconify {
        color: $white;
      }

      &:hover {
        background-color: $primary-dark;
        border-color: $primary-dark;

        .iconify {
          color: $accent-color;
        }
      }
    }
  }

  // 剩余页数徽章样式
  .quota-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 10px;
    border-radius: 999px;
    background: rgba($primary-color, 0.1);
    color: $primary-color;
    text-decoration: none;
    font-size: 12px;
    border: 1px solid rgba($primary-color, 0.2);
    transition: all 0.2s ease;

    &:hover {
      background: rgba($primary-color, 0.15);
      border-color: rgba($primary-color, 0.3);
    }
  }

  // VIP 徽章样式已整合至头像（UserDropdown.vue）
}

@keyframes skeleton-loading {
  0% { background-position: 100% 50%; }
  100% { background-position: 0 50%; }
}

// 中等宽度时适当压缩间距，缓解拥挤
@media (max-width: 1280px) {
  .nav { gap: 0; }
  .user-actions { gap: $spacing-xs; }
  .user-actions .btn { padding: $spacing-xs $spacing-sm; }
  .user-actions .quota-pill { padding: 4px 8px; }
}

// 响应式设计
@media (max-width: 768px) {
  .header-container {
    padding: 0 10px; // 移动端保持两侧10px内边距
  }

  .nav {
    gap: 0; // 移动端同样去掉gap

    .nav-link {
      padding: $spacing-xs $spacing-sm;
      font-size: 14px;
    }
  }

  .user-actions {
    gap: $spacing-xs;

    .btn {
      padding: $spacing-xs $spacing-sm;
      font-size: 12px;
    }
  }

  .logo .logo-text {
    font-size: 1rem;
  }
}

@media (max-width: 480px) {
  .nav {
    display: none; // 移动端隐藏导航，后续可实现汉堡菜单
  }
}
</style>
